import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Image, Row, Card, Divider, Tooltip, Col, notification } from 'antd';
import { EditOutlined, LeftOutlined, DragOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';

import CreateUpdateSubcategoryForm from '../../components/CreateUpdateSubcategoryForm';

import { useFetch } from '../../hooks';
import { LayoutContext } from '../../contexts';
import { URLS } from '../../constants';
import { getImageFallbackUrl, getMenuId } from '../../utils/helpers';
import DndList from '../../components/DndList/DndList';
import axios from 'axios';
import store from 'store';
import { baseURL } from '../../utils/helpers';

const authToken = store.get('authenticationToken');

const { Meta } = Card;

function SubcategoryDetail(
  {
    // history,
    // match: {
    //   params: { id: subcategoryId },
    // },
  }
) {
  const { id } = useParams();
  const subcategoryId = id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setHeaderComponent } = useContext(LayoutContext);
  const [sortMode, setSortMode] = useState(false);
  const [tempSortedList, setTempSortedList] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { doFetch: updateSubcategoryFetch } = useFetch();
  const { doFetch: uploadSubcategoryImageFetch } = useFetch();
  const { doFetch: fetchSort } = useFetch();
  const { doFetch: fetchLanguages } = useFetch();
  const { doFetch: fetchCategories } = useFetch();
  const { doFetch: fetchSubcategories } = useFetch();
  const { doFetch: fetchSubcategoryDetails } = useFetch();

  // Data states
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [languageList, setLanguageList] = useState([]);
  const [categoryList, setCategoryList] = useState([]);
  const [subcategoryList, setSubcategoryList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Search states
  const [searchCategories, setSearchCategories] = useState([]);
  const [isSearchingCategories, setIsSearchingCategories] = useState(false);

  // Load all required data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        const menuId = getMenuId();

        if (!menuId) {
          console.error('Menu ID not found');
          setIsLoading(false);
          return;
        }

        // Load all data in parallel
        const promises = [
          // Languages
          new Promise((resolve, reject) => {
            fetchLanguages({
              url: URLS.languages,
              onSuccess: resolve,
              onError: reject,
            });
          }),

          // Subcategory details (includes products)
          new Promise((resolve, reject) => {
            fetchSubcategoryDetails({
              url: `${URLS.subcategoryDetails(subcategoryId)}?get_products=true`,
              onSuccess: resolve,
              onError: reject,
            });
          }),

          // Categories (for the form)
          new Promise((resolve, reject) => {
            fetchCategories({
              url: URLS.getAllCategories(menuId),
              onSuccess: resolve,
              onError: reject,
            });
          }),

          // Subcategories (for the form, might not be needed but keeping for consistency)
          new Promise((resolve, reject) => {
            fetchSubcategories({
              url: URLS.getAllSubcategories(menuId),
              onSuccess: resolve,
              onError: reject,
            });
          }),
        ];

        const [languagesData, subcategoryDetailsResponse, categoriesResponse, subcategoriesResponse] = await Promise.all(promises);

        // Transform subcategory details to match expected format
        const subcategoryData = subcategoryDetailsResponse.subcategory;
        if (subcategoryData && Array.isArray(subcategoryData.subcategory_translations)) {
          // Create flattened name properties for form fields
          const nameFields = {};
          subcategoryData.subcategory_translations.forEach(translation => {
            nameFields[`name.${translation.language_id}`] = translation.name_text;
          });

          const transformedSubcategory = {
            id: subcategoryData.id,
            categoryId: subcategoryData.category_id,
            ...nameFields, // Include flattened name fields for form
            translations: subcategoryData.subcategory_translations.map(translation => ({
              lanId: translation.language_id,
              lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
              name: translation.name_text,
            })),
            image: subcategoryData.image_url,
            productsCount: 0,
          };
          setSelectedSubcategory(transformedSubcategory);
        }

        // Transform categories list
        let transformedCategories = [];
        if (categoriesResponse.categories) {
          transformedCategories = categoriesResponse.categories.map(cat => ({
            id: cat.id,
            translations: cat.category_translations.map(translation => ({
              lanId: translation.language_id,
              lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
              name: translation.name_text,
            })),
            image: cat.image_url,
            tags: [],
            productsCount: 0,
          }));
          setCategoryList(transformedCategories);
        }

        // Transform subcategories list if available
        let transformedSubcategories = [];
        if (subcategoriesResponse.subcategories) {
          transformedSubcategories = subcategoriesResponse.subcategories.map(sub => ({
            id: sub.id,
            categoryId: sub.category_id,
            translations: sub.subcategory_translations.map(translation => ({
              lanId: translation.language_id,
              lanName: languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
              name: translation.name_text,
            })),
            image: sub.image_url,
            productsCount: 0,
          }));
          setSubcategoryList(transformedSubcategories);
        }

        setLanguageList(languagesData);

        // Extract and transform products from subcategory details response
        if (subcategoryData && subcategoryData.products) {
          // Transform products to include name and nameTranslations fields
          const transformedProducts = subcategoryData.products.map(product => {
            // Find the primary name (prefer first language, fallback to any available translation)
            const primaryTranslation = product.product_translations?.find(
              translation => translation.language_id === languagesData[0]?.id
            ) || product.product_translations?.[0];

            const productName = primaryTranslation?.name_text || 'Unnamed Product';

            // Create nameTranslations HTML
            const nameTranslationsHtml = product.product_translations?.reduce((acc, translation) => {
              const languageName = languagesData.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`;
              acc += `${languageName}: ${translation.name_text}<br />`;
              return acc;
            }, '') || 'No translations available';

            return {
              ...product,
              name: productName,
              nameTranslations: nameTranslationsHtml,
            };
          });
          setProductList(transformedProducts);
        } else {
          setProductList([]);
        }

        setHeaderComponent(<b>{`Subcategory ID: ${subcategoryId}`}</b>);

      } catch (error) {
        console.error('Error loading subcategory detail data:', error);
        setProductList([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    return () => {
      setHeaderComponent(null);
    };
  }, [subcategoryId, fetchLanguages, fetchSubcategoryDetails, fetchCategories, fetchSubcategories]);

  const uploadSubcategoryImage = async (subcategoryData, formValues) => {
    if (!formValues?.image?.file?.originFileObj) {
      notification.success({
        placement: 'bottomRight',
        message: 'Subcategory updated successfully.',
        duration: 3,
      });
      // Invalidate subcategories cache to refetch the list with updated subcategory
      queryClient.invalidateQueries({ queryKey: ['subcategories'] });
      queryClient.invalidateQueries({ queryKey: ['subcategory', subcategoryId] });
      return;
    }

    await uploadSubcategoryImageFetch({
      method: 'FILE_POST',
      url: URLS.uploadSubcategoryImage({ subcategoryId: subcategoryId }),
      params: { image: formValues?.image?.file?.originFileObj },
      showSuccessNotification: true,
      successMessage: 'Subcategory updated successfully.',
      onSuccess: () => {
        // Invalidate subcategories cache to refetch the list with updated subcategory
        queryClient.invalidateQueries({ queryKey: ['subcategories'] });
        queryClient.invalidateQueries({ queryKey: ['subcategory', subcategoryId] });
        navigate('/subcategories');
      },
    });
  };

  const updateSubcategory = async formValues => {
    const translationsObject = languageList.reduce((acc, language) => {
      acc[language.id] = { name_text: formValues[`name.${language.id}`] };
      return acc;
    }, {});

    await updateSubcategoryFetch({
      url: URLS.updateSubcategory({ subcategoryId }),
      method: 'PUT',
      params: { ...translationsObject, category_id: formValues.categoryId },
      onSuccess: data => uploadSubcategoryImage(data, formValues),
    });
  };

  const handleSearchCategories = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 1) {
      setSearchCategories([]);
      return;
    }

    setIsSearchingCategories(true);
    try {
      const response = await axios.get(
        `${baseURL}${URLS.searchCategories()}`,
        {
          params: { q: searchQuery, per_page: 20 },
          headers: { Authorization: authToken },
        }
      );

      // Transform search results to match the structure of original categoryList
      let transformedCategories = (response.data.categories || []).map(cat => ({
        id: cat.id,
        translations: cat.category_translations.map(translation => ({
          lanId: translation.language_id,
          lanName: languageList.find(lang => lang.id === translation.language_id)?.name || `Language ${translation.language_id}`,
          name: translation.name_text,
        })),
        image: cat.image_url,
        tags: [],
        productsCount: 0,
      }));

      setSearchCategories(transformedCategories);
    } catch (error) {
      console.error('Error searching categories:', error);
      setSearchCategories([]);
    } finally {
      setIsSearchingCategories(false);
    }
  }, [languageList]);

  const handleSortChange = newList => {
    setTempSortedList(newList);
    setHasUnsavedChanges(true);
  };

  const handleSaveSortOrder = () => {
    fetchSort({
      url: URLS.productPrioritiesForSubcategoryUpdate({ subcategoryId }),
      method: 'POST',
      params: {
        priorities: tempSortedList.map(product => product.id),
      },
      onSuccess: () => {
        setSortMode(false);
        setHasUnsavedChanges(false);
        setTempSortedList([]);
        // Update the local product list with the new order
        setProductList(tempSortedList);
      },
    });
  };

  const enterSortMode = () => {
    setSortMode(true);
    setTempSortedList([...productList]);
    setHasUnsavedChanges(false);
  };

  const exitSortMode = () => {
    setSortMode(false);
    setTempSortedList([]);
    setHasUnsavedChanges(false);
  };

  if (isLoading) {
    return (
      <Row>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          Loading subcategory details...
        </div>
      </Row>
    );
  }

  return (
    <>
      <Row>
        {selectedSubcategory?.id && (
          <CreateUpdateSubcategoryForm
            selectedSubcategoryItem={selectedSubcategory}
            tagList={[]} // Tags not currently used in subcategory detail
            categoryList={categoryList}
            languageList={languageList}
            onSubmit={updateSubcategory}
            // Search functionality
            searchCategories={searchCategories}
            isSearchingCategories={isSearchingCategories}
            onSearchCategories={handleSearchCategories}
          />
        )}
      </Row>
      <Divider />
      <div className="m-b-30" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        {!sortMode ? (
          <Button
            type="primary"
            icon={<DragOutlined />}
            onClick={enterSortMode}
          >
            Sort Products
          </Button>
        ) : (
          <>
            <Button
              type="dashed"
              icon={<LeftOutlined />}
              onClick={exitSortMode}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleSaveSortOrder}
              disabled={!hasUnsavedChanges}
            >
              Save Order
            </Button>
            {hasUnsavedChanges && (
              <span style={{ color: '#faad14', fontSize: '14px' }}>
                You have unsaved changes
              </span>
            )}
          </>
        )}
      </div>
      <Row gutter={[20, 20]} style={{ overflowX: 'auto' }}>
        <DndList
          list={sortMode ? tempSortedList : productList}
          onChange={handleSortChange}
          sortMode={sortMode}
          renderItem={product => (
            <Col {...(sortMode ? { style: { width: '220px' } } : { span: 4 })} key={product.id}>
              <Card
                className="product-card"
                cover={
                  <Image alt="product image" className="product-card-image" src={product.image_url} fallback={getImageFallbackUrl(product.image_url)} />
                }
                actions={[
                  <Tooltip placement="top" title="Update">
                    <Link to={`/product/${product.id}`}>
                      <EditOutlined type="edit" key="edit" />
                    </Link>
                  </Tooltip>,
                ]}
              >
                <Meta
                  title={
                    <Tooltip
                      placement="top"
                      title={
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product.nameTranslations,
                          }}
                        />
                      }
                    >
                      {product.name}
                    </Tooltip>
                  }
                  description={`${product.price} ${product.currency}`}
                />
              </Card>
            </Col>
          )}
        />
      </Row>
    </>
  );
}

export default SubcategoryDetail;
